import { useEffect } from 'react'

import { useBreakpoint, Breakpoint } from '~/utils/useBreakpoint'
import { ExternalLink } from '~/components/ExternalLink'
import { CookieTableMobil } from '~/static-pages/Cookies/components/CookieTableMobil'

const tableHeader = ['Cookie', 'Utsteder', 'Formål', 'Valgfrihet', 'Mottaker', 'Lagringstid']
const tableBody: Array<{
  name: string
  issuedBy: string
  optional: string
  purpose: string
  receiver: string
  storageTime: string
}> = [
  {
    name: 'Hotjar',
    issuedBy: 'Hotjar Ltd.',
    purpose:
      'Analyseverktøy som benyttes til å optimalisere din brukeropplevelse. Lagrer din IP-adresse i anonymisert form.',
    optional: 'Valgfri',
    receiver: 'House of Math',
    storageTime: '30 dager',
  },
  {
    name: 'Google Analytics',
    issuedBy: 'Google LLC',
    purpose: 'Analyseverktøy som benyttes til å utarbeidestatistikk. Lagrer deler av din IP-adresse.',
    optional: 'Valgfri',
    receiver: 'House of Math',
    storageTime: 'Google lagrer opplysningene avhengig av hvordan du har konfigurert innstillingene dine. Les mer i ',
  },
  {
    name: 'Facebook Pixel',
    issuedBy: 'Facebook Inc.',
    purpose: 'Bedriftsverktøy for å spore bruk av nettsiden og annonseoptimalisering.',
    optional: 'Valgfri',
    receiver: 'House of Math',
    storageTime: 'Ingen fastsatt lagringstid',
  },
]

export function Cookies(): React.ReactElement {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  const isLarge = useBreakpoint() < Breakpoint.LG
  const mainParagraph =
    'px-6 pt-4 pb-4 xl:p-6 xl:pb-6 whitespace-pre-line text-about xl:text-about-large text-gray-sub-text'
  const subParagraph = 'px-6 pb-4 xl:p-6 xl:pb-6 whitespace-pre-line text-about xl:text-about-large text-gray-sub-text'
  const sectionText = 'w-full px-6 pt-6 mx-auto text-lg text-black'
  const subSectionText = 'w-full px-6 pt-6 mx-auto text-md text-black"'
  const sharedTableClassName = 'text-left p-3 text-xs w-1/5'

  return (
    <div className="mt-12 xl:mt-16 px-4 sm:px-24 xl:px-64 lg:pb-16">
      <section className="space-y-8">
        <div className="relative bg-white m-auto rounded shadow-experimental px-2 sm:px-4 xl:px-6 pt-4 sm:pt-6 xl:pt-10 pb-16 md:max-w-screen-md">
          {/* <Header text={t('introHeader')} />sss */}
          <div className="absolute top-0 w-11/12 left-1/2 transform -translate-y-1/2 -translate-x-1/2">
            <div className="rounded bg-purple block">
              {/* {t('title')} */}
              <h1 className="w-full text-center font-semibold text-lg sm:text-h3 xl:text-h2 text-white">
                Cookie Policy
              </h1>
            </div>
          </div>
          <div className={sectionText}>
            {/* {t('title')} */}
            1. Hva er cookies?
          </div>
          <p className={mainParagraph}>
            Vi bruker informasjonskapsler (cookies) på nettsidene våre. En cookie er en tekstfil som lagres i din
            nettleser når du åpner en nettside.
          </p>

          <p className={subParagraph}>
            Når du besøker houseofmath.com installerer vi informasjonskapsler som er nødvendige for at du skal kunne
            benytte deg av nettsidene våre. Dersom du samtykker, kan vi også bruke informasjonskapsler til andre formål,
            slik som statistikk og analyse eller brukervennlighet. Du kan trekke tilbake samtykket når som helst. Merk
            at bruken av cookies kan i noen tilfeller innebære behandling av personopplysninger om deg. Se nedenfor for
            mer informasjon og en oversikt over valgfrie og nødvendige informasjonskapsler.
          </p>
          <p className={subParagraph}>
            Vi anvender også informasjonskapsler gjennom vår tilstedeværelse i sosiale medier. Når du besøker våre sider
            i sosiale medier samler den sosiale mediekanalen inn personopplysninger som benyttes til å utarbeide
            statistikk. House of Math har ikke tilgang til de personopplysningene som samles inn, men vi får tilgang til
            anonymisert statistikk som er utarbeidet av den aktuelle sosiale mediekanalen på basis av de innsamlede
            opplysningene.
          </p>
          <p className={subParagraph}>
            Bruk av cookies reguleres i ekomloven § 2-7b. Vår behandling av personopplysninger innhentet gjennom bruk av
            cookies reguleres av personvernreglene. Mer informasjon om vår behandling av personopplysninger er også
            tilgjengelig i vår personvernerklæring som er tilgjengelig her.
          </p>

          <div className={sectionText}>
            {/* {t('title')} */}
            2. Oversikt over cookies på houseofmath.com
          </div>
          <p className={mainParagraph}>
            Hvilke cookies vi bruker, og hvor vidt personopplysninger behandles ved bruk av disse cookiesene er
            spesifisert nedenfor:
          </p>
          {isLarge && (
            <div>
              {tableBody.map((element) => (
                <CookieTableMobil
                  issuedBy={element.issuedBy}
                  key={element.name}
                  name={element.name}
                  optional={element.optional}
                  purpose={element.purpose}
                  receiver={element.receiver}
                  storageTime={element.storageTime}
                  tableTitles={tableHeader}
                />
              ))}
            </div>
          )}
          {!isLarge && (
            <div>
              <table className="table-fixed border-separate px-6 pb-6">
                <thead>
                  <tr>
                    <th className="text-left text-xs w-auto">{tableHeader[0]}</th>
                    <th className={sharedTableClassName}>{tableHeader[1]}</th>
                    <th className={sharedTableClassName}>{tableHeader[2]}</th>
                    <th className={sharedTableClassName}>{tableHeader[3]}</th>
                    <th className={sharedTableClassName}>{tableHeader[4]}</th>
                    <th className={sharedTableClassName}>{tableHeader[5]}</th>
                  </tr>
                </thead>
                <tbody className="align-baseline">
                  {tableBody.map((element) => {
                    return (
                      <tr key={element.name}>
                        {Object.entries(element).map(([key, field]) => (
                          <td className="text-xxs p-3" key={key}>
                            {field}
                            {key === 'storageTime' && element.name === 'Google Analytics' && (
                              <ExternalLink href="https://policies.google.com/privacy?hl=no">
                                Googles retningslinjer for personvern.
                              </ExternalLink>
                            )}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className={subParagraph}>
            * Dersom du samtykker, vil valgfrie cookies lagres inntil du trekker samtykket tilbake. Valgfrie cookies vil
            uansett slettes automatisk etter 90 dager dersom du ikke har besøkt nettsidene våre i dette tidsrommet.
          </p>

          <div className={sectionText}>
            {/* {t('title')} */}
            3. Mer informasjon om hver enkelt cookie
          </div>
          <div className={subSectionText}>
            {/* {t('title')} */}
            3.1 Google Analytics
          </div>

          <p className={mainParagraph}>
            House of Math benytter analyseverktøyet Google Analytics som bruker cookies plassert på vår nettside. Med
            bruk av analyseverktøyet Google Analytics får vi blant annet informasjon om hvilke sider som er besøkt og
            hvordan våre brukere navigerer mellom disse, hvilke nettsteder brukerne kommer fra og hvilke enheter som
            benyttes pånettstedet. Dette betyr at din IP-adresse vil bli registrert når du besøker nettsiden vår, for å
            gi oss informasjon om trafikk og bruksmønstre, men vi vil ikke bruke denne informasjonen til å identifisere
            deg. Din IP-adresse anonymiseres (den siste åttebitgruppen i IP-adressen fjernes) før informasjon lagres og
            benyttes for å utarbeide statistikk som blant annet benyttes til å utvikle og forbedre nettsiden.
          </p>
          <p className={subParagraph}>
            Opplysninger som samles inn gjennom Google Analytics er lagret på Googles servere i USA, og er underlagt{' '}
            <ExternalLink href="https://policies.google.com/privacy?hl=no">Googles personvernerklæring.</ExternalLink>
          </p>

          <p className={subParagraph}>
            Behandlingsgrunnlaget for å registrere din IP-adresse før den anonymiseres er House of Maths berettigede
            interesse i å sikre optimal drift av nettsiden.
          </p>
          <div className={subSectionText}>
            {/* {t('title')} */}
            3.2 Hotjar
          </div>

          <p className={mainParagraph}>
            House of Math benytter webanalyseverktøyet Hotjar som bruker cookies plassert på vår nettside. Dette
            verktøyet gir oss mulighet til å forstå ditt behov som bruker av nettstedet og gjør oss i stand til å
            optimalisere din brukeropplevelse. Ved hjelp av Hotjar kan vi blant annet se hvor mye tid du bruker på
            hvilke sider, hvilke lenker du velger å klikke på og hvor langt du scroller på nettsiden. I tillegg benyttes
            Hotjar for innsamling av svar på spørreundersøkelser. Hotjar lagrer informasjon om brukerne i en anonymisert
            brukerprofil. Verken House of Math eller Hotjar vil bruke denne informasjonen til å identifisere deg.
          </p>

          <p className={subParagraph}>
            Din IP-adresse blir benyttet for å bestemme i hvilket land du befinner deg, og blir lagret i anonymisert
            form på Hotjar sine servere i Irland. For unntaksvis logging har Hotjar underleverandører som befinner seg
            utenfor EU.
          </p>
          <p className={subParagraph}>
            Behandlingsgrunnlaget for å registrere din IP-adresse før den anonymiseres er House of Maths berettigede
            interesse i å sikre optimal drift av nettsiden.
          </p>

          <div className={subSectionText}>
            {/* {t('title')} */}
            3.3 Facebook Pixel
          </div>
          <p className={mainParagraph}>
            House of Math benytter analyseverktøyet Facebook Pixel som bruker cookies plassert på vår nettside. Dette
            betyr at Facebook får informasjon vedrørende ditt besøk på houseofmath.com. Ut ifra denne informasjonen
            tilpasses annonser som vises til deg på Facebook. Du har som Facebook-bruker mulighet til å endre dine
            annonsepreferanser for din Facebook-konto.
          </p>
          <p className={subParagraph}>
            Facebook Pixel registrerer blant annet IP-adressen din, informasjon om nettleseren din, knapper du har
            klikket på, osv. All data blir aggregert før den blir brukt. Aggregert data er opplysninger som er slått
            sammen slik at det alene basert på disse dataene ikke er mulig å identifisere enkeltpersoner.
          </p>
          <p className={subParagraph}>
            Behandlingsgrunnlaget for å registrere din IP-adresse før den anonymiseres er ditt samtykke. Dette kan når
            som helst trekkes tilbake, se punkt 4 nedenfor.
          </p>

          {/*
            <div className={sectionText}>
              {// {t('title')} }
            4. Administrere cookies
          </div>
          <p className={mainParagraph}>
            Dersom du har godtatt eller reservert deg mot enkelte cookies på våre nettsider, kan du endre disse
            innstillingene ved å klikke på «administrer cookies» nederst på sidene
          </p>
          */}
          <p className={subParagraph}>
            Du kan blokkere og tillate cookies gjennom innstillingene i nettleseren. Les mer om hvordan du kan gjøre
            dette på{' '}
            <ExternalLink href="https://nettvett.no/slik-administrer-du-informasjonskapsler/">Nettvett.no</ExternalLink>{' '}
            sine hjemmesider.
          </p>
        </div>
      </section>
    </div>
  )
}
